import { Directive, ElementRef, HostBinding, HostListener } from '@angular/core';

/**
 * Makes table rows keyboard accessible by adding tabindex, ARIA role,
 * and keyboard event handlers (Enter/Space keys).
 *
 * The directive dispatches a MouseEvent on keyboard activation,
 * allowing existing (click) handlers in templates to work for both
 * mouse and keyboard users while maintaining proper event flow
 * (including loading indicators, etc.).
 *
 * Usage:
 * <tr appClickableRow (click)="onRowClick(item)">
 *
 * @example
 * <tr appClickableRow (click)="viewUser(userSummary)">
 *   <td>{{userSummary.name}}</td>
 * </tr>
 */
@Directive({
    selector: '[appClickableRow]'
})
export class ClickableRowDirective {
    @HostBinding('attr.tabindex') tabindex = '0';
    @HostBinding('attr.role') role = 'button';
    @HostBinding('style.cursor') cursor = 'pointer';

    constructor(private el: ElementRef) {}

    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    onKeyPress(event: KeyboardEvent): void {
        event.preventDefault();
        // Dispatch a proper MouseEvent to trigger the full event flow
        const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
        });
        this.el.nativeElement.dispatchEvent(clickEvent);
    }
}
